import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ClipboardCheckIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { JobStatusTransitionConfirmationDialog } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useUpdateJob } from "@/hooks/mutations";
import { useSetJob } from "@/hooks/sets";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { Job, JobStatus } from "@/types";

const screeningQuestionSchema = z.object({
  id: z.number(),
  question: z.string().min(1, "Required"),
  required: z.boolean(),
});

const JobPrescreeningQuestionsForm = ({ job }: { job: Job }) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const setJob = useSetJob();
  const successToast = useSuccessToast();
  const { mutate: updateJob } = useUpdateJob();

  // TODO: Hack to compute the next id for the new screening question.
  // Increments on top of the last id in the existing screening questions.
  const computeNextId = () => {
    const screeningQuestions = form.getValues("screeningQuestions");
    if (screeningQuestions.length === 0) return 0;
    return screeningQuestions[screeningQuestions.length - 1].id + 1;
  };

  const form = useForm<{
    screeningQuestions: z.infer<typeof screeningQuestionSchema>[];
  }>({
    defaultValues: {
      screeningQuestions: job.screeningQuestions?.screeningQuestions ?? [],
    },
    resolver: zodResolver(
      z.object({
        screeningQuestions: z.array(screeningQuestionSchema),
      }),
    ),
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "screeningQuestions",
  });

  const handleSubmit = (values: {
    screeningQuestions: z.infer<typeof screeningQuestionSchema>[];
  }) => {
    if (job.status === JobStatus.ACTIVE) {
      setIsConfirmationDialogOpen(true);
      return;
    }

    handleConfirm(values);
  };

  const handleConfirm = (values: {
    screeningQuestions: z.infer<typeof screeningQuestionSchema>[];
  }) => {
    updateJob(
      {
        request: {
          ...job,
          screeningQuestions: {
            screeningQuestions: values.screeningQuestions,
          },
        },
      },
      {
        onSuccess: (data) => {
          setJob({ job: data });
          setIsConfirmationDialogOpen(false);
          successToast({ description: "Pre-screening questions updated!" });
        },
      },
    );
  };

  return (
    <>
      <Form {...form}>
        <form
          className="flex flex-col gap-6"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {fields.map((field, index) => (
            <div className="flex items-center gap-2" key={field.id}>
              <div className="flex w-full gap-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <FormField
                  control={form.control}
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  name={`screeningQuestions.${index}.question`}
                  render={({ field }) => (
                    <FormItem className="flex w-full items-center gap-2 space-y-0 text-slate-700">
                      <FormLabel className="shrink-0">
                        Question {index + 1}
                      </FormLabel>
                      <FormControl>
                        <Textarea className="resize-none" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <FormField
                    control={form.control}
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    name={`screeningQuestions.${index}.required`}
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0 text-slate-700">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel>Required</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <Button
                onClick={() => {
                  remove(index);
                }}
                type="button"
                variant="ghost"
              >
                <TrashIcon />
                Remove
              </Button>
            </div>
          ))}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <Button
              onClick={() => {
                append({ id: computeNextId(), question: "", required: false });
              }}
              type="button"
              variant="ghost"
            >
              <PlusIcon className="h-4 w-4" /> Add question
            </Button>
          </div>
          <div className="flex justify-end">
            <Button type="submit">
              <ClipboardCheckIcon className="h-4 w-4" />
              Save
            </Button>
          </div>
        </form>
      </Form>
      <JobStatusTransitionConfirmationDialog
        // Passing in the `REVIEW` status ensures that the status transition
        // confirmation dialog is shown for the -> `REVIEW` transition which
        // automatically occurs when an active job is edited.
        jobStatus={JobStatus.REVIEW}
        onConfirm={() => {
          handleConfirm(form.getValues());
        }}
        onOpenChange={setIsConfirmationDialogOpen}
        open={isConfirmationDialogOpen}
      />
    </>
  );
};

export const JobPrescreeningQuestions = ({
  className,
  job,
}: {
  className?: string;
  job: Job;
}) => {
  return (
    <div className={className}>
      <JobPrescreeningQuestionsForm job={job} />
    </div>
  );
};
